/// <amd-module name="Wit/SprintsHub/SprintsView/Components/Taskboard/TaskboardCard" />
import { ITeamSettings } from "Agile/Contracts/Common";
import { ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { usePageContext, useService } from "VSS/Platform/Layout";
import { IMenuItem, MenuButton } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { IClientWorkItem, IObservableClientWorkItem } from "Wit/ClientWorkItemService/IClientWorkItem";
import { DraggableCard, divyFields } from "Wit/Common/Controls/Boards/Card";
import { DragMode, IPosition, ScrollableData } from "Wit/Common/Controls/Boards/Draggable";
import { OnChangeType } from "Wit/Common/Controls/Boards/EditableField";
import { FieldLayout } from "Wit/Common/Controls/Boards/Interfaces";
import { ValidationError } from "Wit/Common/Controls/Boards/ValidationError";
import { CoreFieldRefNames, WorkItemTypes } from "Wit/Common/Generated/Constants";
import { IHotKeyEventData, IHotKeyEventService, IHotKeyEventType } from "Wit/Common/HotKeyEventService/IHotKeyEventService";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { ISprintTaskboardData, ITaskboardWorkItem } from "Wit/SprintsHub/Common/Contracts/Interfaces";
import { openWorkItemFormDialog } from "Wit/SprintsHub/SprintsView/Components/Util";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { ITaskboardService } from "Wit/SprintsHub/SprintsView/Services/ITaskboardService";
import { WorkItemTypeColorAndIcon } from "Wit/UI/Components/WorkItemTypeIcon";
import * as React from "react";
import { getWorkItemUrl } from "../../Utils/RoutingUtils";
import { addContributedMenuItems, getDefaultMenuItems, getDeleteDialog } from "../Util";

export interface ITaskboardCardProps {
    columnId?: string;
    isNewItem: boolean;
    isMoveDisabled?: () => boolean;
    onNewItemCancel?: () => void;
    onNewItemCreate?: (enterPressed: boolean, id: number) => void;
    remainingWorkText?: string;
    rowId?: string;
    showChildRollup?: boolean;
    taskboardData: ISprintTaskboardData | undefined;
    workItem: IObservableClientWorkItem<ITaskboardWorkItem>;
    workItemTypeIconData: WorkItemTypeColorAndIcon | undefined;
    onCardChange: OnChangeType;
    onCardClick: (workItemId: number) => void;
    onCardDrag: (
        e: PointerEvent,
        card: HTMLElement,
        mode: DragMode,
        position: IPosition,
        workItem: IObservableClientWorkItem<IClientWorkItem>,
        startingColumnId: string,
        startingRowId: string,
        isParent?: boolean
    ) => undefined;
    onCardFocus?: (event: React.FocusEvent<HTMLElement>) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>, item: IObservableClientWorkItem<IClientWorkItem>) => void;
    scrollableData?: ScrollableData;
    ignoreRemainingWorkFieldValue?: boolean;
    canChangeRemainingWork?: boolean;
}

export const TaskboardCard: React.FunctionComponent<ITaskboardCardProps> = ({
    columnId,
    isNewItem,
    isMoveDisabled,
    onCardChange,
    onCardClick,
    onCardDrag,
    onCardFocus,
    onKeyDown,
    onNewItemCancel,
    onNewItemCreate,
    remainingWorkText,
    rowId,
    scrollableData,
    showChildRollup,
    taskboardData,
    workItem,
    workItemTypeIconData,
    ignoreRemainingWorkFieldValue,
    canChangeRemainingWork
}) => {
    const taskboardService = useService<ITaskboardService>("ITaskboardService");
    const pageContext = usePageContext();

    const hotKeyEventService = pageContext.getService<IHotKeyEventService>("IHotKeyEventService");

    const deleteItemChangedCallback = async ({ workItemId, projectName }: IHotKeyEventData) => {
        if (workItemId && workItemId == workItem.value.id) {
            getDeleteDialog(pageContext, workItemId, projectName ?? "");
        }
    };

    React.useEffect(() => {
        hotKeyEventService.subscribe(deleteItemChangedCallback, IHotKeyEventType.WorkItemDelete);
        return () => hotKeyEventService.unsubscribe(deleteItemChangedCallback, IHotKeyEventType.WorkItemDelete);
    }, []);

    if (!workItem) {
        // return unparented card
        return (
            <div className="flex-row wit-card taskboard-card unparented-card depth-8">
                <div className="card-flag flex-noshrink unparented-flag" />
                <div className="unparented-icon" />
                <div className="unparented-title">{Resources.Unparented}</div>
                {remainingWorkText && (
                    <div className="remaining-work font-size font-weight-semibold flex-self-end padding-8">
                        <Tooltip overflowOnly>
                            <div className="text-ellipsis">{remainingWorkText}</div>
                        </Tooltip>
                    </div>
                )}
            </div>
        );
    }

    const cardSettingsOriginal = taskboardData?.boardCardSettings.cards[workItem.value.getFieldValue<string>(CoreFieldRefNames.WorkItemType)!];
    const cardSettings = cardSettingsOriginal ? [...cardSettingsOriginal] : [];
    let showEmptyFields = true;
    const showEmptyFieldsIndex = cardSettings.findIndex(cardSetting => cardSetting.hasOwnProperty("showEmptyFields"));
    if (showEmptyFieldsIndex > -1) {
        showEmptyFields = cardSettings.splice(showEmptyFieldsIndex, 1)[0].showEmptyFields !== "false";
    }
    let fieldLayouts: FieldLayout[] = [];
    if (taskboardData?.taskboardModel.fieldDefinitions) {
        fieldLayouts = cardSettings.map(field => {
            const fieldDefinition = taskboardData.taskboardModel.fieldDefinitions.find(
                definition => definition.ReferenceName === field.fieldIdentifier
            )!;

            return {
                displayName: fieldDefinition.Name,
                referenceName: fieldDefinition.ReferenceName,
                isIdentity: fieldDefinition.IsIdentity,
                isEditable: fieldDefinition.IsEditable
            };
        });
    }

    const { coreFields, additionalFields } = divyFields(fieldLayouts);
    return (
        <Observer grayedOut={workItem.value.grayedOut} observedWorkItem={workItem}>
            {({ grayedOut, observedWorkItem }) => {
                const isWorkItemInSameIterationAsTaskboard =
                    observedWorkItem.getFieldValue<string>(CoreFieldRefNames.IterationPath) === taskboardData?.iterationPath;
                return (
                    <DraggableCard
                        key={workItem.value.id}
                        className={css("taskboard-card body-s", (grayedOut || !isWorkItemInSameIterationAsTaskboard) && "taskboard-card-monochrome")}
                        cardFieldsCollapsed={false}
                        isNew={isNewItem}
                        itemLayout={{
                            workItem: workItem,
                            coreFields: coreFields,
                            additionalFields: additionalFields,
                            url: getWorkItemUrl(pageContext, workItem.value.id),
                            workItemTypeColorAndIcon: workItemTypeIconData!
                        }}
                        isTitleEditing={workItem.value.isTitleEditing}
                        showChildRollup={!!showChildRollup}
                        showEmptyFields={!!showEmptyFields}
                        compact={false}
                        isMoveDisabled={isMoveDisabled}
                        full={false}
                        onDrag={
                            rowId && userCanDragTaskboardCard(taskboardData)
                                ? (e: PointerEvent, card: HTMLElement, mode: DragMode, position: IPosition) =>
                                      onCardDrag(e, card, mode, position, workItem, columnId ?? "", rowId, /** isParent */ !columnId)
                                : undefined
                        }
                        onDragComplete={
                            rowId && userCanDragTaskboardCard(taskboardData)
                                ? (e: PointerEvent, card: HTMLElement, mode: DragMode, position: IPosition) =>
                                      onCardDrag(e, card, mode, position, workItem, columnId ?? "", rowId, /** isParent */ !columnId)
                                : undefined
                        }
                        onChange={onCardChange}
                        onFocus={onCardFocus}
                        onKeyDown={onKeyDown}
                        onNewItemCreate={(title: string, enterPressed: boolean) =>
                            onCreate(taskboardService, title, workItem, enterPressed, onNewItemCreate, onNewItemCancel)
                        }
                        onNewItemCancel={onNewItemCancel}
                        onRenderAdditionalContent={renderAdditionalContent}
                        onWorkItemClick={(
                            e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement>,
                            workItemId: number
                        ) => {
                            if (
                                e.ctrlKey ||
                                e.metaKey ||
                                e.shiftKey ||
                                ((e as React.MouseEvent<HTMLAnchorElement, MouseEvent>).button !== undefined &&
                                    (e as React.MouseEvent<HTMLAnchorElement, MouseEvent>).button !== 0) ||
                                e.defaultPrevented
                            ) {
                                return;
                            }

                            onCardClick(workItemId);
                            e.preventDefault();
                        }}
                        scrollableData={scrollableData}
                        tagStyles={undefined}
                        remainingWorkField={ignoreRemainingWorkFieldValue ? undefined : taskboardData?.taskboardModel.workRollupField}
                        remainingWorkText={remainingWorkText}
                        resizeDisabled
                        onRenderMenuButton={
                            !isNewItem
                                ? (pageContext: IVssPageContext, item: any): JSX.Element => (
                                      <TaskboardCardMenuButton
                                          onCardClick={onCardClick}
                                          item={item}
                                          hasPermissionToDeleteWorkItem={taskboardService.hasPermissionToDeleteWorkItems()}
                                      />
                                  )
                                : undefined
                        }
                        workItemType={workItem.value.getFieldValue(CoreFieldRefNames.WorkItemType) as WorkItemTypes}
                        canChangeRemainingWork={canChangeRemainingWork}
                    />
                );
            }}
        </Observer>
    );
};

function userCanDragTaskboardCard(taskboardData: ISprintTaskboardData | undefined): boolean {
    return !!taskboardData;
}

async function onCreate(
    taskboardService: ITaskboardService,
    title: string,
    workItem: IObservableClientWorkItem<ITaskboardWorkItem>,
    enterPressed: boolean,
    onCreate?: (enterPressed: boolean, id: number) => void,
    onCancel?: () => void
) {
    if (!title) {
        onCancel && onCancel();
    } else {
        const type = workItem.value.getFieldValue<string>(CoreFieldRefNames.WorkItemType) || "";
        await taskboardService.createNewWorkItem(type, title, workItem, workItem.value.id);
        onCreate && onCreate(enterPressed, workItem.value.id);
    }
}

function getMenuItems(
    menuItems: ObservableCollection<IMenuItem>,
    pageContext: IVssPageContext,
    onCardClick: (workItemId: number) => void,
    teamSettings: ITeamSettings | undefined,
    currentItem: IObservableClientWorkItem<ITaskboardWorkItem>,
    hasPermissionToDeleteWorkItem: boolean
): void {
    menuItems.push(getDefaultMenuItems(pageContext, onCardClick, teamSettings, currentItem, hasPermissionToDeleteWorkItem).value);
    addContributedMenuItems(pageContext, menuItems, currentItem.value.id, currentItem.value.getFieldValue(CoreFieldRefNames.WorkItemType) as string);
}

function renderAdditionalContent(observableItem: IObservableClientWorkItem): JSX.Element {
    return <ValidationError observableItem={observableItem} onOpenWorkItemForm={openWorkItemFormDialog} />;
}

interface ITaskboardCardMenuButton {
    onCardClick: (workItemId: number) => void;
    item: IObservableClientWorkItem<ITaskboardWorkItem>;
    hasPermissionToDeleteWorkItem: ObservableValue<boolean>;
}

function TaskboardCardMenuButton({ onCardClick, item, hasPermissionToDeleteWorkItem }: ITaskboardCardMenuButton) {
    const pageContext = usePageContext();
    const menuItems = React.useRef<ObservableCollection<IMenuItem>>(new ObservableCollection<IMenuItem>());
    const teamSettingsSvc = pageContext.getService<ITeamSettingsService>("ITeamSettingsService");

    return (
        <Observer
            teamSettings={{
                observableValue: teamSettingsSvc.getTeamSettingsObservable(),
                filter: (value: ITeamSettings | undefined) => {
                    menuItems.current = new ObservableCollection<IMenuItem>();
                    return true;
                }
            }}
            hasPermissionToDeleteWorkItem={hasPermissionToDeleteWorkItem}
        >
            {(observerProps: { teamSettings: ITeamSettings | undefined; hasPermissionToDeleteWorkItem: boolean }) => (
                <MenuButton
                    onExpand={() => {
                        if (menuItems.current.length === 0) {
                            getMenuItems(
                                menuItems.current,
                                pageContext,
                                onCardClick,
                                observerProps.teamSettings,
                                item,
                                observerProps.hasPermissionToDeleteWorkItem
                            );
                        }
                    }}
                    buttonClassName="flex-grow"
                    className={"card-context-menu"}
                    hideDropdownIcon={true}
                    iconProps={{ className: "small", iconName: "More" }}
                    contextualMenuProps={{
                        menuProps: {
                            id: "card-context-menu",
                            items: menuItems.current
                        }
                    }}
                    subtle
                />
            )}
        </Observer>
    );
}
